import {
  DocumentReference,
  DocumentSnapshot,
  Query,
  QuerySnapshot,
  Timestamp,
} from "@atgof-firebase/types";
import { comparePotentiallyNumbered, UNKNOWN_LABEL } from "./util";
import dayjs from "dayjs";
import { TimeOfDay } from "./phrases";
import { EpisodePath, parseEpisodePath } from "./episode";
import { OnNext, onSnapshot } from "./onSnapshot";
import { HasScripts } from "./has-scripts";
import { Team } from "./model/project/team";
import { notDeleted } from "./general";
import _ from "lodash";

export type SceneData = {
  id: string;
  bdsId: string;
  sheetNumber: string;
  synopsis: string;
  sequence: string;
  episodeId: string;
  baseSceneId?: string;
  isInterior: boolean;
  isExterior: boolean;
  timeOfDay?: TimeOfDay;
  sets: string[] | undefined;
  location: string;
  scriptDay: string;
  scriptDate?: Date;
  scriptPageNumbers: string;
  pages: string;
  unit: string;
  comment: string;
  cast: string[] | undefined;
  nonCast?: {
    sa: string[];
  };
  team?: string;
  filmingDate?: Date;
  orderInFilmingDate?: number;
  orderInStory?: number;
};

export interface TimingData {
  pa?: number;
  rehearsal?: number;
  shot?: number;
  edit?: number;
}

export interface Scene extends SceneData, HasScripts {
  ref: DocumentReference;
  deleted?: Timestamp | false;
  teamRef?: DocumentReference;
  teamData?: Team;
  isComplete?: boolean;
  isDropped?: boolean;
  actions?: { isDone: boolean; description: string }[];
  continuousFilmingSequence?: DocumentReference[];
  extras?: string[]; // TODO This is never set
  timings?: TimingData;
  ignoreInTimings?: boolean;
  paNotes?: string;
  director?: string;
  readyForEdit?: boolean;
  editComplete?: boolean;
}

type ScenePath = EpisodePath & { scene?: string; orderInStory?: number };

export function parseScenePath(
  path: string | undefined,
  orderInStory?: number,
): ScenePath {
  // path is of the form ${EPISODE_PATH}/scenes/SCENE_ID
  const parts = path?.split("/");
  return {
    ...parseEpisodePath(path),
    scene: parts && parts?.length > 7 ? parts[7] : undefined,
    orderInStory,
  };
}

export function fullSceneIdentifier(
  episodeId: string | undefined,
  id: string | undefined,
) {
  if (!(episodeId && id)) return;
  return `${episodeId}_${id}`;
}

export const canonicalDateFormat = "YYYY-MM-DD";

export function canonicalDate(d: dayjs.Dayjs) {
  return d.format(canonicalDateFormat);
}

export function filmingDateIdentifier(filmingDate: Date | undefined) {
  if (!filmingDate) return;
  return canonicalDate(dayjs(filmingDate));
}

export function onScenesInContinuousFilmingSequence(
  sceneRef: DocumentReference | undefined,
  onNext: OnNext<DocumentReference[] | undefined>,
) {
  return onSnapshot<DocumentSnapshot, DocumentReference[]>(
    (onNext) => sceneRef?.onSnapshot(onNext),
    (scene) => scene.get("continuousFilmingSequence") ?? [sceneRef],
    onNext,
  );
}

export function getScenePath(
  scene: DocumentSnapshot | DocumentReference | Scene | undefined,
) {
  return parseScenePath(
    scene ? ("path" in scene ? scene.path : scene.ref.path) : undefined,
    scene && "orderInStory" in scene ? scene.orderInStory : undefined,
  );
}

export const compareSceneIds = comparePotentiallyNumbered;

function compareScenePaths(a: ScenePath, b: ScenePath) {
  const res = comparePotentiallyNumbered(a.episode, b.episode);
  return res
    ? res
    : comparePotentiallyNumbered(
        a.orderInStory ?? a.scene,
        b.orderInStory ?? b.scene,
      ) ||
        (b.orderInStory !== undefined ? 1 : 0) -
          (a.orderInStory !== undefined ? 1 : 0);
}

export function compareScenes(
  a: Scene | DocumentReference,
  b: Scene | DocumentReference,
) {
  return compareScenePaths(getScenePath(a), getScenePath(b));
}

export function sortScenes<T extends Scene | DocumentReference>(scenes: T[]) {
  return scenes.sort(compareScenes);
}

export function toScene(doc: DocumentSnapshot, teams?: DocumentSnapshot[]) {
  const data = doc.data();
  const teamRef = doc.get("team") as DocumentReference | undefined;
  const teamData = teams?.find((doc) => doc.ref.path === teamRef?.path)?.data();
  const filmingDate = (
    doc.get("filmingDate") as Timestamp | undefined
  )?.toDate();
  const scriptDate = (doc.get("scriptDate") as Timestamp | undefined)?.toDate();
  return {
    ...data,
    id: doc.id,
    deleted: doc.get("deleted"),
    ref: doc.ref,
    path: doc.ref.path,
    team: teamRef?.id,
    teamRef,
    teamData,
    filmingDate,
    scriptDate,
  } as Scene;
}

export function onScenes(
  sceneOrDayScenes: DocumentSnapshot[] | undefined,
  teams: DocumentSnapshot[] | undefined,
  onNext: (next: Scene[] | undefined) => void,
): () => void {
  // TODO Refactor using onSnapshot.ts
  let unsubs: (() => void)[] = [];
  onNext(undefined);
  if (sceneOrDayScenes) {
    if (!sceneOrDayScenes.length) onNext([]);
    const vals = new Map<string, Scene>();
    function setVal(origDoc: DocumentSnapshot, doc: DocumentSnapshot) {
      vals.set(origDoc.ref.path, toScene(doc, teams));
      if (vals.size == sceneOrDayScenes?.length) {
        const next = sceneOrDayScenes?.map(({ ref }) => vals.get(ref.path));
        onNext(!next?.includes(undefined) ? (next as Scene[]) : undefined);
      }
    }
    for (const doc of sceneOrDayScenes) {
      const sceneRef: DocumentReference | undefined = doc.get("scene");
      if (sceneRef) unsubs.push(sceneRef.onSnapshot((d) => setVal(doc, d)));
      else setVal(doc, doc);
    }
  }
  return () => {
    for (const unsub of unsubs) unsub();
  };
}

export function scenesCollection(parent: DocumentReference | undefined) {
  if (!parent) return;
  return parent.collection("scenes");
}

export function scenesQuery(parent: DocumentReference | undefined) {
  const coll = scenesCollection(parent);
  if (!coll) return;
  return notDeleted(coll);
}

export function onScenesInParent(
  parent: DocumentReference,
  onNext: OnNext<Scene[] | undefined>,
  queryTransformer?: (q: Query) => Query,
) {
  let q = scenesQuery(parent)!;
  if (queryTransformer) q = queryTransformer(q);
  return onSnapshot(
    _.bind(q.onSnapshot, q),
    ({ docs }: QuerySnapshot) => sortScenes(docs.map((doc) => toScene(doc))),
    onNext,
  );
}

export function sceneLabel(path: string) {
  const m = path.match(/episodes\/([^/]+)\/scenes\/([^/]+)$/);
  const standalone = m && m[1] === "-";
  return (
    (standalone ? "" : m ? m[1] : UNKNOWN_LABEL) +
    "/" +
    (m ? m[2] : UNKNOWN_LABEL)
  );
}
